import { useState } from "react";

/**
 * Tracks attempt usage for a quiz that has a configured `maxAttempts`.
 * Quizzes without a positive `maxAttempts` (legacy content, or scheduled/proctored
 * quizzes which are always capped to 1 attempt at the config level) are treated
 * as "not limited" here so the retake UI simply stays hidden for them.
 */
export const useQuizAttempts = ({
  maxAttempts,
  attemptsUsed: initialAttemptsUsed = 0,
  hasPassed: initialHasPassed = false,
}) => {
  const [attemptsUsed, setAttemptsUsed] = useState(Number(initialAttemptsUsed) || 0);
  const [isRetaking, setIsRetaking] = useState(false);
  const [hasPassed, setHasPassed] = useState(Boolean(initialHasPassed));

  const normalizedMax = Number(maxAttempts);
  // maxAttempts of 1 (or unset) is the "old logic" quiz — No Attempt Tracking, no retake,
  // no attempts UI shown at all. Only quizzes that opted into "Allow Multiple Attempts"
  // (maxAttempts > 1) light up this layer.
  const isLimited = Number.isFinite(normalizedMax) && normalizedMax > 1;
  const remainingAttempts = isLimited ? Math.max(0, normalizedMax - attemptsUsed) : null;
  // Once the student has passed, retaking is no longer offered even if attempts remain.
  const canRetake = isLimited && remainingAttempts > 0 && !hasPassed;

  const startRetake = () => setIsRetaking(true);

  const recordAttempt = (passed = false) => {
    setAttemptsUsed((prev) => prev + 1);
    setIsRetaking(false);
    if (passed) setHasPassed(true);
  };

  return {
    maxAttempts: isLimited ? normalizedMax : null,
    attemptsUsed,
    remainingAttempts,
    isLimited,
    canRetake,
    hasPassed,
    isRetaking,
    startRetake,
    recordAttempt,
  };
};
